import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlay, FiArrowRight, FiInstagram } from '../common/Icons';

const VideoEmbed = ({ id, featured = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative w-full overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900/50 shadow-2xl group ${featured ? 'md:max-w-4xl mx-auto' : ''}`}
      style={{ paddingBottom: '56.25%', height: 0 }}
    >
      {!isPlaying ? (
        <div
          className="absolute inset-0 cursor-pointer w-full h-full bg-cover bg-center"
          style={{ backgroundImage: `url(https://img.youtube.com/vi/${id}/maxresdefault.jpg)` }}
          onClick={() => setIsPlaying(true)}
        >
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
            <div className={`${featured ? 'w-24 h-24' : 'w-16 h-16'} bg-red-600 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
              <SafeIcon icon={FiPlay} className={`${featured ? 'w-10 h-10' : 'w-6 h-6'} text-white fill-current translate-x-1`} />
            </div>
          </div>
        </div>
      ) : (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1`}
          title="YouTube video"
          className="absolute top-0 left-0 w-full h-full border-0"
          allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture;web-share"
          allowFullScreen
        ></iframe>
      )}
    </motion.div>
  );
};

const InstagramReelCard = ({ url, index }) => (
  <motion.a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ y: -5, borderColor: 'rgba(249,115,22,0.5)' }}
    className="block relative aspect-square rounded-3xl overflow-hidden border border-white/5 bg-white/5 backdrop-blur-sm group transition-all duration-300"
  >
    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:bg-orange-500 group-hover:border-orange-400 transition-all duration-500 shadow-xl">
        <SafeIcon icon={FiInstagram} className="w-6 h-6 text-orange-500 group-hover:text-white transition-colors" />
      </div>
      <div className="space-y-1">
        <span className="block text-[10px] font-black text-white/40 uppercase tracking-[0.2em] group-hover:text-orange-500 transition-colors">
          Feedback #{index + 1}
        </span>
        <span className="block text-[9px] font-bold text-white/20 uppercase tracking-widest group-hover:text-white group-hover:opacity-100 transition-all">
          Guarda Video
        </span>
      </div>
    </div>
    <div className="absolute bottom-4 right-4 text-white/10 group-hover:text-orange-500 transition-colors">
      <SafeIcon icon={FiPlay} className="w-3 h-3 fill-current" />
    </div>
  </motion.a>
);

const Testimonials = () => {
  const featuredVideoId = 'eDtrgRkSqWk';

  const primaryCaseStudies = [
    { id: 'OJLSpyzJvLQ', label: 'Caso Studio #1' },
    { id: '9vfLfMGEoM8', label: 'Caso Studio #2' },
    { id: '6F3SDhxxuys', label: 'Caso Studio #3' },
    { id: '1NeKqX3uR_g', label: 'Caso Studio #4' },
  ];

  const secondaryCaseStudies = ['iQYgQOlO8rc', 'XagCkSdT65k', 'QVP91lJeLbk', 'zSlUDIdx098'];

  const igReels = [
    'https://www.instagram.com/reel/DSzZgZrgHSY/',
    'https://www.instagram.com/reel/DM0X3iTMs63/',
    'https://www.instagram.com/reel/DMsryJisuf7/',
  ];

  return (
    <section id="testimonials" className="py-24 bg-black-pure relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter">
            Risultati <span className="text-orange-500 italic">Reali</span>
          </h2>
          <p className="text-slate-400 font-medium text-lg">
            Guarda le storie di chi ha già trasformato il proprio business utilizzando i nostri sistemi AI.
          </p>
        </div>

        {/* Featured */}
        <div className="mb-24">
          <h3 className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] italic text-center mb-8">
            Case Study In Primo Piano
          </h3>
          <VideoEmbed id={featuredVideoId} featured={true} />
        </div>

        {/* Primary Case Studies */}
        <div className="mb-24">
          <div className="flex items-center justify-center space-x-4 mb-12">
            <div className="h-[1px] w-12 bg-orange-500/30"></div>
            <h3 className="text-white font-black uppercase tracking-[0.3em] text-[10px] italic">
              Testimonianze Principali
            </h3>
            <div className="h-[1px] w-12 bg-orange-500/30"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {primaryCaseStudies.map(({ id, label }) => (
              <div key={id}>
                <p className="text-[9px] font-black text-orange-500/60 uppercase tracking-[0.3em] mb-3 text-center">
                  {label}
                </p>
                <VideoEmbed id={id} />
              </div>
            ))}
          </div>
        </div>

        {/* Secondary Case Studies */}
        <div className="mb-32">
          <div className="flex items-center justify-center space-x-4 mb-10">
            <div className="h-[1px] w-12 bg-white/10"></div>
            <h3 className="text-white/30 font-black uppercase tracking-[0.4em] text-[10px] italic">
              Altri Casi Studio
            </h3>
            <div className="h-[1px] w-12 bg-white/10"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {secondaryCaseStudies.map((id) => (
              <VideoEmbed key={id} id={id} />
            ))}
          </div>
        </div>

        {/* Instagram Testimonials */}
        <div className="mb-20">
          <div className="flex items-center justify-center space-x-4 mb-12">
            <div className="h-[1px] w-12 bg-orange-500/30"></div>
            <h3 className="text-orange-500 font-black uppercase tracking-[0.3em] text-[10px] italic">
              Ulteriori Testimonianze
            </h3>
            <div className="h-[1px] w-12 bg-orange-500/30"></div>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {igReels.map((url, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <InstagramReelCard url={url} index={index} />
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="flex justify-center mt-12"
        >
          <button
            onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            className="btn-premium text-white px-12 py-5 rounded-2xl font-black text-xl flex items-center space-x-3 shadow-[0_0_50px_rgba(249,115,22,0.3)] hover:shadow-[0_0_70px_rgba(249,115,22,0.5)] transition-all duration-300"
          >
            <span>Sblocca i Modelli</span>
            <SafeIcon icon={FiArrowRight} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;